import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {Page, Pageable} from '../../../model/rest/base';
import {
  ConfirmationRequestStatus,
  RequestType,
  V3ManualConfirmationRequest,
  V3SchoolConfirmationRequestExtended,
  V3Student,
} from '../../../model/v3/schools';
import {PersonInvitation, V3PersonInvitationDetails, VerifiedEmailChangeRequest,} from '../../../model/v3/people';
import {appendCollection, appendIfDefined, createPageableParams} from './rest-v3-utils';

@Injectable({
  providedIn: 'root',
})
export class WorkflowsRestService {
  constructor(private http: HttpClient) {
  }

  private emailChangeUrl(schoolId: number, path: string): string {
    return `${environment.eventsApiBase}/v1/workflows/email-change/schools/${schoolId}${path}`;
  }

  private confirmationUrl(schoolId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/workflows/confirmation-requests/schools/${schoolId}${path}`;
  }


  requestStudentEmailChange(
    schoolId: number,
    studentId: number,
    request: VerifiedEmailChangeRequest,
  ): Observable<void> {
    return this.http.post<void>(this.emailChangeUrl(schoolId, `/students/${studentId}`), request);
  }

  requestTeacherEmailChange(
    schoolId: number,
    teacherId: number,
    request: VerifiedEmailChangeRequest,
  ): Observable<void> {
    return this.http.post<void>(this.emailChangeUrl(schoolId, `/teachers/${teacherId}`), request);
  }

  findConfirmationRequests(
    schoolId: number,
    role: RequestType[],
    status: ConfirmationRequestStatus[],
    pageable?: Pageable,
  ): Observable<Page<V3SchoolConfirmationRequestExtended>> {
    let params = createPageableParams(pageable);
    params = appendCollection(params, 'role', role);
    params = appendCollection(params, 'status', status);
    return this.http.get<Page<V3SchoolConfirmationRequestExtended>>(this.confirmationUrl(schoolId), {
      params,
    });
  }

  inviteStudent(
    schoolId: number,
    invitation: PersonInvitation<V3PersonInvitationDetails>,
  ): Observable<V3Student> {
    return this.http.post<V3Student>(this.confirmationUrl(schoolId, '/students'), invitation);
  }

  inviteTeacher(
    schoolId: number,
    invitation: PersonInvitation<V3PersonInvitationDetails>,
  ): Observable<void> {
    return this.http.post<void>(this.confirmationUrl(schoolId, '/teachers'), invitation);
  }

  inviteManager(
    schoolId: number,
    invitation: PersonInvitation<V3PersonInvitationDetails>,
  ): Observable<void> {
    return this.http.post<void>(this.confirmationUrl(schoolId, '/managers'), invitation);
  }

  confirmRequestManually(
    schoolId: number,
    requestType: RequestType,
    email: string,
    status: ConfirmationRequestStatus,
    request: V3ManualConfirmationRequest,
  ): Observable<void> {
    const params = new HttpParams()
      .set('requestType', requestType)
      .set('email', email)
      .set('status', status);
    return this.http.put<void>(this.confirmationUrl(schoolId, '/confirm-manually'), request, {
      params,
    });
  }

  renewConfirmationRequest(
    schoolId: number,
    requestType: RequestType,
    email: string,
    status: ConfirmationRequestStatus,
  ): Observable<void> {
    let params = new HttpParams();
    params = appendIfDefined(params, 'requestType', requestType);
    params = appendIfDefined(params, 'email', email);
    params = appendIfDefined(params, 'status', status);
    return this.http.put<void>(this.confirmationUrl(schoolId, '/renew'), null, {params});
  }

  revokeConfirmationRequest(
    schoolId: number,
    requestType: RequestType,
    email: string,
    status: ConfirmationRequestStatus,
  ): Observable<void> {
    let params = new HttpParams();
    params = appendIfDefined(params, 'requestType', requestType);
    params = appendIfDefined(params, 'email', email);
    params = appendIfDefined(params, 'status', status);
    return this.http.put<void>(this.confirmationUrl(schoolId, '/revoke'), null, {params});
  }

}
